import React from 'react';
import { Button, DatePicker } from '@roger/ui-components';
import { Check, Edit, Loader2 } from 'lucide-react';

import {
  formatDateForDisplay,
  formatDateToISO,
  parseISOToLocalDate,
} from '../../utils/dateUtils';

/** A setting's value, or the spinner while loading and "Unavailable" after a failed read. */
export const SettingValue: React.FC<{
  isLoading: boolean;
  loadFailed: boolean;
  children: React.ReactNode;
}> = ({ isLoading, loadFailed, children }) => {
  if (isLoading) {
    return (
      <Loader2
        className='h-4 w-4 shrink-0 animate-spin text-muted-foreground'
        aria-label='Loading setting'
      />
    );
  }
  if (loadFailed) {
    return (
      <span className='shrink-0 whitespace-nowrap text-sm font-medium text-muted-foreground'>
        Unavailable
      </span>
    );
  }
  return <>{children}</>;
};

/** A clearable picker over an ISO date string. */
export const IsoDatePicker: React.FC<{
  id?: string;
  label: string;
  value: string | null;
  className?: string;
  onChange: (value: string | null) => void;
}> = ({ id, label, value, className, onChange }) => (
  <DatePicker
    id={id}
    className={className}
    datePickerSize='sm'
    date={value ? (parseISOToLocalDate(value) ?? undefined) : undefined}
    onDateChange={date => onChange(date ? formatDateToISO(date) : null)}
    showClearButton
    aria-label={label}
  />
);

/** An ISO date setting: a clearable picker while editing, else the date or "Not set". */
export const SettingDateValue: React.FC<{
  id: string;
  label: string;
  value: string | null;
  editing: boolean;
  isLoading: boolean;
  loadFailed: boolean;
  onChange: (value: string | null) => void;
}> = ({ id, label, value, editing, isLoading, loadFailed, onChange }) => (
  <SettingValue isLoading={isLoading} loadFailed={loadFailed}>
    {editing ? (
      <IsoDatePicker
        id={id}
        label={label}
        value={value}
        className='w-full md:w-48'
        onChange={onChange}
      />
    ) : (
      <span className='shrink-0 whitespace-nowrap text-sm font-medium'>
        {value ? formatDateForDisplay(value) : 'Not set'}
      </span>
    )}
  </SettingValue>
);

/** Edit, or Cancel and Save Changes while editing. Nothing for a viewer. */
export const SettingsEditFooter: React.FC<{
  canEdit: boolean;
  isEditing: boolean;
  isSaving: boolean;
  editDisabled: boolean;
  saveDisabled?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({
  canEdit,
  isEditing,
  isSaving,
  editDisabled,
  saveDisabled = false,
  onEdit,
  onCancel,
  onSave,
}) => {
  if (!canEdit) return null;
  return (
    <div className='flex justify-end gap-2'>
      {isEditing ? (
        <>
          <Button variant='outline' onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving || saveDisabled}>
            {isSaving ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Saving...
              </>
            ) : (
              <>
                <Check className='mr-2 h-4 w-4' />
                Save Changes
              </>
            )}
          </Button>
        </>
      ) : (
        <Button variant='outline' onClick={onEdit} disabled={editDisabled}>
          <Edit className='mr-2 h-4 w-4' />
          Edit
        </Button>
      )}
    </div>
  );
};
